import React, { Suspense } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SEO as Seo } from "@/components/seo/SEO";
import { RootLayout } from "@/components/layout/RootLayout";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { CalculatorActivePanel } from "@/components/calculators/CalculatorActivePanel";
import { CalculatorsPanelSkeleton } from "@/components/calculators/CalculatorsPanelSkeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { getCalculatorSeo, isPublicCalculatorSlug } from "@/lib/calculatorSeo";

const HomeLoanCalculator = lazyWithRetry(
  () => import("@/components/calculators/HomeLoanCalculator")
);
const PFCalculator = lazyWithRetry(() => import("@/components/calculators/PFCalculator"));
const PPFCalculator = lazyWithRetry(() => import("@/components/calculators/PPFCalculator"));
const SIPCalculator = lazyWithRetry(() => import("@/components/calculators/SIPCalculator"));
const IncomeTaxCalculator = lazyWithRetry(
  () => import("@/components/calculators/IncomeTaxCalculator")
);
const RetirementCalculator = lazyWithRetry(
  () => import("@/components/calculators/RetirementCalculator")
);
const InsuranceCalculator = lazyWithRetry(
  () => import("@/components/calculators/InsuranceCalculator")
);
const EmergencyFundCalculator = lazyWithRetry(
  () => import("@/components/calculators/EmergencyFundCalculator")
);

function CalculatorSuspense({ children }: Readonly<{ children: React.ReactNode }>) {
  return <Suspense fallback={<CalculatorsPanelSkeleton />}>{children}</Suspense>;
}

/**
 * Render Public Calculator.
 */
function renderPublicCalculator(slug: string) {
  switch (slug) {
    case "incometax":
      return (
        <CalculatorSuspense>
          <IncomeTaxCalculator />
        </CalculatorSuspense>
      );
    case "homeloan":
      return (
        <CalculatorSuspense>
          <HomeLoanCalculator />
        </CalculatorSuspense>
      );
    case "retirement":
      return (
        <CalculatorSuspense>
          <RetirementCalculator />
        </CalculatorSuspense>
      );
    case "insurance":
      return (
        <CalculatorSuspense>
          <InsuranceCalculator />
        </CalculatorSuspense>
      );
    case "emergency":
      return (
        <CalculatorSuspense>
          <EmergencyFundCalculator />
        </CalculatorSuspense>
      );
    case "pf":
      return (
        <CalculatorSuspense>
          <PFCalculator />
        </CalculatorSuspense>
      );
    case "ppf":
      return (
        <CalculatorSuspense>
          <PPFCalculator />
        </CalculatorSuspense>
      );
    case "sip":
      return (
        <CalculatorSuspense>
          <SIPCalculator />
        </CalculatorSuspense>
      );
    default:
      return null;
  }
}

/**
 * Public Calculator Cta.
 */
function PublicCalculatorCta() {
  return (
    <div className="rounded-lg border border-orange-100 bg-orange-50/80 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <p className="font-medium text-gray-900">Track family wealth on TellerWand</p>
        <p className="text-sm text-gray-600 mt-1">
          Save calculator results and sync household PPF, EPF, mutual funds, and shared goals in one
          family dashboard.
        </p>
      </div>
      <PostLoginAuthLink mode="signup">
        <Button className="shrink-0">Start free</Button>
      </PostLoginAuthLink>
    </div>
  );
}

/**
 * Public SEO landing page for a single financial calculator.
 */
export default function PublicCalculatorPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user } = useAuth();

  if (!isPublicCalculatorSlug(slug)) {
    return <Navigate to="/404" replace />;
  }

  const seo = getCalculatorSeo(slug);
  if (!seo) {
    return <Navigate to="/404" replace />;
  }
  const calculatorPanel = renderPublicCalculator(slug);
  const Layout = user ? DashboardLayout : RootLayout;
  const backHref = "/calculators";
  const backLabel = "All calculators";

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={`/calculators/${slug}`}
        structuredData={[
          {
            type: "product",
          },
        ]}
      />

      <Layout>
        <div className="container max-w-5xl mx-auto space-y-4 sm:space-y-6 py-4 sm:py-6 px-3 sm:px-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link to={backHref}>
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </Link>
            </Button>
          </div>

          {!user && <PublicCalculatorCta />}

          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{seo.h1}</h1>

          <Card className="overflow-hidden">
            <CardContent className="p-3 sm:p-4 md:p-6 min-w-0">
              <CalculatorActivePanel>{calculatorPanel}</CalculatorActivePanel>
            </CardContent>
          </Card>

          {!user && (
            <p className="text-sm text-muted-foreground text-center pb-4">
              Free calculator — no sign-in required.{" "}
              <Link
                to="/auth?mode=signup"
                className="text-primary underline-offset-4 hover:underline"
              >
                Create an account
              </Link>{" "}
              to save results and track your portfolio.
            </p>
          )}
        </div>
      </Layout>
    </>
  );
}
